// src/pages/admin/billing/AdminBillingQuotesPage.js
import React from 'react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminData } from '../../../contexts/AdminDataContext';
import QuoteForm from '../../../components/admin/billing/quote/QuoteForm';
import InvoiceForm from '../../../components/admin/billing/invoice/InvoiceForm';
import PDFGenerator from '../../../utils/PDFGenerator';
import pb from '../../../lib/pocketbase';

function AdminBillingQuotesPage() {
  const { loading: adminLoading, customers: ctxCustomers, vehicles: ctxVehicles, services: ctxServices, products: ctxProducts, quotes: ctxQuotes } = useAdminData() || {};
  const [allowEmpty, setAllowEmpty] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [editingQuote, setEditingQuote] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [error, setError] = React.useState('');

  const [settings, setSettings] = React.useState({ quote_prefix: 'DEV', quote_counter: 1, default_tax_rate: 20, quote_validity_days: 30 });
  const [settingsId, setSettingsId] = React.useState(null);
  const [customers, setCustomers] = React.useState(() => Array.isArray(ctxCustomers) ? ctxCustomers : []);
  const [vehicles, setVehicles] = React.useState(() => Array.isArray(ctxVehicles) ? ctxVehicles : []);
  const [services, setServices] = React.useState(() => Array.isArray(ctxServices) ? ctxServices.map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false })) : []);
  const [parts, setParts] = React.useState(() => Array.isArray(ctxProducts) ? ctxProducts.map(p => ({ id: p.id, name: p.name || p.title || 'Produit', price: Number(p.price || p.base_price || 0), sku: p.sku || '' })) : []);
  const [quotes, setQuotes] = React.useState(() => {
    if (Array.isArray(ctxQuotes)) {
      return ctxQuotes.map(q => ({ ...q, customer_name: q.customer_name || q.expand?.customer_id?.name || '—' }));
    }
    return [];
  });
  const [showQuotesGeneralQR, setShowQuotesGeneralQR] = React.useState(false);
  const [historyQuote, setHistoryQuote] = React.useState(null);
  const [menuFor, setMenuFor] = React.useState(null);
  const [quoteLogs, setQuoteLogs] = React.useState([]);
  const [quoteLogsLoading, setQuoteLogsLoading] = React.useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = React.useState(false);
  const [editingInvoice, setEditingInvoice] = React.useState(null);
  const [prefillInvoiceQuoteId, setPrefillInvoiceQuoteId] = React.useState('');
  const [defaultCustomerIdFromQuery, setDefaultCustomerIdFromQuery] = React.useState('');

  const statusLabel = (s) => ({
    draft: 'Brouillon',
    sent: 'Envoyé',
    accepted: 'Accepté',
    rejected: 'Refusé',
    expired: 'Expiré',
  })[s] || (s || 'Brouillon');

  const customerNameOf = React.useCallback((q) => (
    q?.customer_name || q?.expand?.customer_id?.name || '—'
  ), []);

  const companyNameOf = React.useCallback((q) => (
    q?.expand?.customer_id?.company || ''
  ), []);

  const projectNameOf = React.useCallback((q) => {
    const pid = q?.project_id || q?.vehicle_id;
    if (!pid) return '';
    const p = vehicles.find(v => v.id === pid);
    return p?.name || p?.brand || '';
  }, [vehicles]);

  const buildQuoteShareText = (link) => (
    `Bonjour, votre devis est prêt !\n\n` +
    `Pour le consulter et le valider, rendez-vous dans votre espace Mon Compte à partir de ce lien sécurisé : ${link}\n` +
    `Si vous avez la moindre question, répondez simplement à ce message — nous sommes là pour vous accompagner.\n\n` +
    `Bien à vous,\n` +
    `L’équipe INFOCOLAB`
  );

  const buildQuoteDetailedShareText = (quote, accountUrl) => {
    const lines = [
      `Bonjour,`,
      ``,
      `Votre devis ${quote.quote_number || ''} est disponible dans votre espace Mon Compte :`,
      accountUrl,
      ``,
      `Montant TTC : ${typeof quote.total === 'number' ? quote.total.toFixed(2) : '0.00'} €`,
      `Valide jusqu'au : ${quote.valid_until ? new Date(quote.valid_until).toLocaleDateString() : '-'}`,
      ``,
      `Merci de valider le devis depuis votre espace.`,
      ``,
      `Bien à vous,`,
      `L’équipe INFOCOLAB`
    ];
    return lines.join('\n');
  };

  const loadQuoteHistory = async (quote) => {
    if (!quote?.id) return;
    setQuoteLogsLoading(true);
    try {
      const logs = await pb.collection('activity_logs').getFullList({
        filter: `collection = "quotes" && record_id = "${quote.id}"`,
        sort: '-created'
      }).catch(() => []);
      setQuoteLogs(logs || []);
    } catch (e) {
      console.error("Erreur chargement historique devis:", e?.message || e);
      setQuoteLogs([]);
    } finally {
      setQuoteLogsLoading(false);
    }
  };

  const handleCreateInvoice = async (data) => {
    try {
      const created = await pb.collection('invoices').create(data);
      try { alert('Facture créée'); } catch { }
      setShowInvoiceForm(false);
      setEditingInvoice(null);
      setPrefillInvoiceQuoteId('');
      return created;
    } catch (e) {
      console.error('Create invoice failed', e?.data || e?.message || e);
      const details = e?.data?.data ? JSON.stringify(e.data.data) : (e?.message || 'Erreur inconnue');
      try { alert(`Création facture impossible:\n${details}`); } catch { }
      throw e;
    }
  };

  const handleQuotePDF = async (quote, mode = 'preview') => {
    try {
      // Récupérer customer & vehicle si possible
      let customer = customers.find(c => c.id === quote.customer_id) || null;
      if (!customer && quote.expand?.customer_id) customer = quote.expand.customer_id;
      let vehicle = vehicles.find(v => v.id === quote.vehicle_id) || null;
      if (!vehicle && quote.expand?.vehicle_id) vehicle = quote.expand.vehicle_id;
      try {
        if (!customer && quote.customer_id) customer = await pb.collection('customers').getOne(quote.customer_id);
      } catch { }
      try {
        if (!vehicle && quote.vehicle_id) vehicle = await pb.collection('projects').getOne(quote.vehicle_id);
      } catch { }

      const generator = new PDFGenerator();
      await generator.generateQuotePDF(quote, customer, vehicle, settings);
      if (mode === 'download') {
        generator.download(`Devis_${quote.quote_number || quote.id}.pdf`);
      } else {
        generator.preview();
      }
    } catch (e) {
      console.error('PDF error:', e);
      try { alert("Génération PDF indisponible pour l'instant"); } catch { }
    }
  };

  React.useEffect(() => {
    (async () => {
      setError('');
      // Settings (store full record so PDF has logo/entreprise/mentions légales)
      try {
        const res = await pb.collection('settings').getList(1, 1, { sort: '-created' });
        const s = res?.items?.[0];
        if (s) { setSettings({ ...s, _baseUrl: pb?.baseUrl }); setSettingsId(s.id); }
      } catch { /* defaults */ }

      // Customers
      try {
        if (Array.isArray(ctxCustomers) && ctxCustomers.length) setCustomers(ctxCustomers);
        else {
          const res = await pb.collection('customers').getList(1, 200, { sort: '-created' });
          setCustomers(res?.items ?? []);
        }
      } catch (e) { setCustomers([]); setError(prev => prev || e?.message || 'Erreur chargement clients'); }

      // Projects (used as 'vehicles' in form state)
      try {
        if (Array.isArray(ctxVehicles) && ctxVehicles.length) setVehicles(ctxVehicles);
        else {
          const res = await pb.collection('projects').getList(1, 200, { sort: '-created' });
          setVehicles(res?.items ?? []);
        }
      } catch { setVehicles([]); }

      // Services (catalogue de prestations)
      try {
        if (Array.isArray(ctxServices) && ctxServices.length) setServices(ctxServices.map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false })));
        else {
          const res = await pb.collection('services_catalog').getList(1, 200, { sort: 'name' });
          const mapped = (res?.items ?? []).map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false }));
          setServices(mapped);
        }
      } catch { setServices([]); }

      // Products (catalogue)
      try {
        if (Array.isArray(ctxProducts) && ctxProducts.length) setParts(ctxProducts.map(p => ({ id: p.id, name: p.name || p.title || 'Produit', price: Number(p.price || p.base_price || 0), sku: p.sku || '' })));
        else {
          const res = await pb.collection('products').getList(1, 200, { sort: 'name' });
          const mapped = (res?.items ?? []).map(p => ({ id: p.id, name: p.name || p.title || 'Produit', price: Number(p.price || p.base_price || 0), sku: p.sku || '' }));
          setParts(mapped);
        }
      } catch { setParts([]); }

      // Quotes list (ensure customer_name is present, independent of customers state to avoid flicker)
      try {
        const mapWithName = (items) => (items || []).map(q => ({
          ...q,
          customer_name: q.customer_name || q.expand?.customer_id?.name || '—',
        }));
        if (Array.isArray(ctxQuotes) && ctxQuotes.length) setQuotes(mapWithName(ctxQuotes));
        else {
          const res = await pb.collection('quotes').getList(1, 200, { sort: '-created', expand: 'customer_id' });
          setQuotes(mapWithName(res?.items ?? []));
        }
      } catch { setQuotes([]); }
    })();
  }, [ctxVehicles, ctxServices, ctxProducts, ctxQuotes]);

  // Deep-link support: /admin/facturation/devis?new=1&customer=<id>
  React.useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const isNew = sp.get('new');
      const cid = sp.get('customer');
      if (isNew === '1') {
        setEditingQuote(null);
        setOpen(true);
        if (cid) setDefaultCustomerIdFromQuery(cid);
      }
    } catch { }
  }, []);

  // Prevent empty-state flash on first refresh: allow empty only after admin finished loading
  React.useEffect(() => {
    if (adminLoading) { setAllowEmpty(false); return; }
    const t = setTimeout(() => setAllowEmpty(true), 250);
    return () => { clearTimeout(t); };
  }, [adminLoading]);

  const refreshQuotes = async () => {
    try {
      const res = await pb.collection('quotes').getList(1, 200, { sort: '-created', expand: 'customer_id' });
      const mapped = (res?.items ?? []).map(q => ({
        ...q,
        customer_name: q.customer_name || q.expand?.customer_id?.name || '—',
      }));
      setQuotes(mapped);
    } catch { }
  };

  const sanitizeQuotePayload = (payload) => {
    const safe = { ...payload };
    // Required relations
    if (!safe.customer_id || typeof safe.customer_id !== 'string') throw new Error('missing_customer');
    // Optional relation alias compatibility
    if (safe.project_id && !safe.vehicle_id) safe.vehicle_id = safe.project_id;
    // Ensure quote_number exists (server requires it)
    if (!safe.quote_number || typeof safe.quote_number !== 'string' || !safe.quote_number.trim()) {
      try {
        const prefix = settings?.quote_prefix || 'DEV';
        const next = String((settings?.quote_counter || 1)).padStart(4, '0');
        safe.quote_number = `${prefix}-${next}`;
      } catch {
        safe.quote_number = `DEV-${String(Date.now()).slice(-4)}`;
      }
    }
    // Dates
    if (!safe.valid_until) delete safe.valid_until;
    // Booleans
    if (typeof safe.tax_enabled !== 'boolean') safe.tax_enabled = true;
    // Numbers
    if (typeof safe.tax_rate !== 'undefined') safe.tax_rate = Number(safe.tax_rate || 0);
    ['subtotal', 'discount_total', 'tax_amount', 'total', 'discount_value'].forEach(k => {
      if (typeof safe[k] !== 'undefined') safe[k] = Number(safe[k] || 0);
    });
    // Items array
    if (Array.isArray(safe.items)) {
      safe.items = safe.items.map((it) => ({
        service_id: it.service_id || '',
        description: it.description || '',
        quantity: Number(it.quantity || 0),
        unit_price: Number(it.unit_price || 0),
        tax_rate: typeof it.tax_rate !== 'undefined' ? Number(it.tax_rate) : Number(settings?.default_tax_rate || 20),
        total: Number(
          typeof it.total !== 'undefined'
            ? it.total
            : (Number(it.quantity || 0) * Number(it.unit_price || 0))
        )
      }));
    } else {
      safe.items = [];
    }
    // Cleanup UI-only flags
    delete safe.allowEmpty;
    return safe;
  };

  const handleCreate = async (payload) => {
    setLoading(true);
    try {
      const { autoGenerateInvoice, ...rest } = payload;
      const created = await pb.collection('quotes').create(sanitizeQuotePayload(rest));

      // Auto-generate invoice if requested
      if (autoGenerateInvoice) {
        try {
          const invPrefix = settings?.invoice_prefix || 'FACT';
          const invCounter = Number(settings?.invoice_counter || 1);
          const nextInvNumber = `${invPrefix}-${String(invCounter).padStart(4, '0')}`;

          const invoicePayload = {
            invoice_number: nextInvNumber,
            customer_id: created.customer_id,
            vehicle_id: created.vehicle_id || created.project_id, // ensure fallback
            project_id: created.project_id || created.vehicle_id,
            quote_id: created.id,
            status: 'draft',
            payment_status: 'pending',
            due_date: (() => {
              const d = new Date(Date.now() + (Number(settings?.invoice_payment_terms) || 30) * 86400000);
              return (!isNaN(d.getTime())) ? d.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
            })(),
            items: created.items || [],
            discount_type: created.discount_type,
            discount_value: created.discount_value,
            discount_label: created.discount_label,
            tax_enabled: created.tax_enabled,
            tax_rate: created.tax_rate,
            notes: created.notes || '',
            subtotal: created.subtotal,
            discount_total: created.discount_total,
            tax_amount: created.tax_amount,
            total: created.total
          };

          await pb.collection('invoices').create(invoicePayload);

          // Increment invoice counter
          if (settingsId) {
            // fetch current settings to be safe? or just optimistic increment
            // We'll trust the current state for now, but to be safer we could just increment.
            await pb.collection('settings').update(settingsId, { invoice_counter: invCounter + 1 });
          }
          alert('Devis et Facture créés avec succès !');
        } catch (invErr) {
          console.error("Auto-invoice creation failed", invErr);
          alert("Devis créé, mais erreur lors de la génération automatique de la facture: " + (invErr.message || invErr));
        }
      }

      if (settingsId) {
        try { await pb.collection('settings').update(settingsId, { quote_counter: Number(settings.quote_counter || 1) + 1 }); } catch { }
      }
      await refreshQuotes();
      setOpen(false);
      setEditingQuote(null);
      return created;
    } catch (e) {
      console.error('Create quote failed', e?.data || e?.message || e);
      let details = '';
      try {
        const d = e?.data?.data || {};
        details = Object.keys(d).map(k => `${k}: ${d[k]?.message || JSON.stringify(d[k])}`).join('\n');
      } catch { }
      try { alert(`Création devis impossible. Vérifiez client, projet et au moins une prestation.${details ? '\n\nDétails:\n' + details : ''}`); } catch { }
      throw e;
    } finally {
      setLoading(false);
    }
  };

  const noop = async () => ({ id: String(Date.now()) });

  const handleUpdate = async (id, payload) => {
    setLoading(true);
    try {
      await pb.collection('quotes').update(id, sanitizeQuotePayload(payload));

      // Auto-generate invoice if status becomes accepted
      if (payload.status === 'accepted') {
        try {
          // Check if invoice exists
          const existing = await pb.collection('invoices').getList(1, 1, { filter: `quote_id="${id}"` });
          if (existing.totalItems === 0) {
            // Fetch full quote data
            const quote = await pb.collection('quotes').getOne(id);

            const invPrefix = settings?.invoice_prefix || 'FACT';
            const invCounter = Number(settings?.invoice_counter || 1);
            const nextInvNumber = `${invPrefix}-${String(invCounter).padStart(4, '0')}`;

            const invoicePayload = {
              invoice_number: nextInvNumber,
              customer_id: quote.customer_id,
              vehicle_id: quote.vehicle_id || quote.project_id,
              project_id: quote.project_id || quote.vehicle_id,
              quote_id: quote.id,
              status: 'draft',
              payment_status: 'pending',
              due_date: (() => {
                const d = new Date(Date.now() + (Number(settings?.invoice_payment_terms) || 30) * 86400000);
                return (!isNaN(d.getTime())) ? d.toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
              })(),
              items: quote.items || [],
              discount_type: quote.discount_type,
              discount_value: quote.discount_value,
              discount_label: quote.discount_label,
              tax_enabled: quote.tax_enabled,
              tax_rate: quote.tax_rate,
              notes: quote.notes || '',
              subtotal: quote.subtotal,
              discount_total: quote.discount_total,
              tax_amount: quote.tax_amount,
              total: quote.total
            };

            await pb.collection('invoices').create(invoicePayload);

            if (settingsId) {
              await pb.collection('settings').update(settingsId, { invoice_counter: invCounter + 1 });
            }
            alert('Devis accepté : Facture générée automatiquement !');
          }
        } catch (err) {
          console.error("Auto-invoice on accept failed", err);
          // Don't block the UI, just log/alert
          alert("Devis accepté, mais échec création facture: " + err.message);
        }
      }

      await refreshQuotes();
      setOpen(false);
      setEditingQuote(null);
    } catch (e) {
      console.error(e);
      alert('Erreur mise à jour: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce devis ?')) return;
    try {
      await pb.collection('quotes').delete(id);
      await refreshQuotes();
    } catch (e) { alert(e?.message || 'Erreur suppression'); }
  };

  return (
    <AdminLayout title="">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <h1 style={{ marginTop: 0 }}>Devis</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <button
            className="btn-primary"
            onClick={() => setOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 10,
              background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
              border: '1px solid rgba(79,70,229,0.6)',
              boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
              color: 'white'
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </span>
            <span>Créer un devis</span>
          </button>
        </div>
      </div>
      <style>{`
        .btn-primary:hover { transform: translateY(-1px); filter: brightness(1.04); }
        .btn-primary:active { transform: translateY(0); filter: brightness(0.98); }
        .header-pill{ cursor:pointer; background: transparent; border:1px solid transparent; color: var(--admin-text); padding:8px 10px; border-radius:10px; }
        .header-pill:hover{ background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
        .admin-shell.admin-light .header-pill:hover{ background: rgba(2,6,23,0.06); }

        /* Avatar (align with Clients) */
        .customer-avatar { width:36px; height:36px; border-radius:999px; display:grid; place-items:center; font-weight:700; background:#3b82f6; color:#fff; }

        /* Button shapes & colors */
        .header-shape{ border-radius:10px; padding:8px 10px; }
        .btn-accent{ background: rgba(79,70,229,0.12); color: var(--admin-accent); border:1px solid rgba(79,70,229,0.45); }
        .btn-accent:hover{ background: rgba(79,70,229,0.22) !important; border-color: rgba(79,70,229,0.6) !important; color: var(--admin-accent) !important; }
        .btn-danger{ background: rgba(239,68,68,0.12); color:#ef4444; border:1px solid rgba(239,68,68,0.45); }
        .btn-danger:hover{ background: rgba(239,68,68,0.22) !important; border-color: rgba(239,68,68,0.6) !important; color:#ef4444 !important; }
        .btn-success{ background: rgba(16,185,129,0.12); color:#10b981; border:1px solid rgba(16,185,129,0.45); }
        .btn-success:hover{ background: rgba(16,185,129,0.22) !important; border-color: rgba(16,185,129,0.6) !important; color:#10b981 !important; }
        .btn-warning{ background: rgba(234,179,8,0.12); color:#eab308; border:1px solid rgba(234,179,8,0.45); }
        .btn-warning:hover{ background: rgba(234,179,8,0.22) !important; border-color: rgba(234,179,8,0.6) !important; color:#eab308 !important; }
        .btn-muted{ background: rgba(148,163,184,0.12); color:#94a3b8; border:1px solid rgba(148,163,184,0.45); }
        .btn-muted:hover{ background: rgba(148,163,184,0.22) !important; border-color: rgba(148,163,184,0.6) !important; color:#94a3b8 !important; }

        /* Tooltips */
        .has-tip{ position:relative; }
        .has-tip::after{ content: attr(data-tip); position:absolute; bottom: calc(100% + 8px); left:50%; transform: translateX(-50%); background: var(--admin-panel); color: var(--admin-text); border:1px solid var(--admin-border); border-radius:8px; padding:6px 8px; font-size:.8rem; white-space:nowrap; box-shadow: 0 8px 18px rgba(0,0,0,0.18); opacity:0; pointer-events:none; transition: opacity .15s ease; z-index: 70; }
        .has-tip::before{ content:""; position:absolute; bottom: calc(100% + 4px); left:50%; transform: translateX(-50%) rotate(45deg); width:10px; height:10px; background: var(--admin-panel); border-left:1px solid var(--admin-border); border-top:1px solid var(--admin-border); opacity:0; transition: opacity .15s ease; z-index: 70; }
        .has-tip:hover::after, .has-tip:hover::before{ opacity:1; }

        /* Search bar (model style) */
        .searchbar{ position:relative; display:flex; align-items:center; gap:8px; }
        .searchbar .icon{ position:absolute; left:10px; color: var(--admin-muted); }
        .searchbar input{ width:260px; background: var(--admin-panel); border:1px solid var(--admin-border); color: var(--admin-text); border-radius:10px; padding:8px 10px 8px 30px; outline:none; transition: box-shadow .15s ease, border-color .15s ease; }
        .searchbar input::placeholder{ color: var(--admin-muted); }
        .searchbar input:focus{ border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
        /* Group container neutral; each button framed */
        .header-actions-frame{ display:flex; gap:8px; padding:0; border:none; background: transparent; }
        .header-actions-frame .header-pill{ border:1px solid var(--admin-border); background: var(--admin-panel-2); }
        .admin-shell.admin-light .header-actions-frame .header-pill:hover{ background: rgba(2,6,23,0.06); }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1001; padding: 20px; }
        .modal-content { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 720px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.35); }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px; border-bottom: 1px solid var(--admin-border); }
        .modal-header h3 { margin: 0; font-size: 1.1rem; }
        .modal-body { padding: 16px 20px; }
        .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 8px; border-radius: 6px; }
        .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }

        /* Mobile tweaks to prevent overflow (mirror Clients page behavior) */
        @media (max-width: 720px) {
          .quotes-header{ flex-wrap: wrap; }
          .quotes-search{ width:100%; margin-left:0 !important; }
          .quotes-search input{ width:100%; }
        }
        @media (max-width: 480px) {
          .quotes-grid{ grid-template-columns: 1fr !important; }
        }
      `}</style>
      {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}

      <div className="card" style={{ marginTop: 12 }}>
        <div className="quotes-header" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div className="header-actions-frame">
            <button className="header-pill header-shape has-tip" data-tip="QR Espace client" title="QR Espace client" aria-label="QR Espace client" onClick={() => setShowQuotesGeneralQR(true)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm12 0h2v2h-2v-2zm-4 0h2v2h-2v-2zm4 4h2v2h-2v-2zm-4 0h2v2h-2v-2z" />
              </svg>
            </button>
            <button className="header-pill header-shape has-tip" data-tip="Copier le lien" onClick={async () => {
              const link = window.location.origin + '/admin/facturation/devis';
              try { await navigator.clipboard.writeText(link); alert('Lien copié'); } catch { }
            }}>Copier le lien</button>
            <button className="header-pill header-shape has-tip" data-tip="Copier le message" onClick={async () => {
              const link = window.location.origin + '/admin/facturation/devis';
              const shareText = buildQuoteShareText(link);
              try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { }
            }}>Copier le message</button>
            <button className="header-pill header-shape has-tip" data-tip="Partager" onClick={async () => {
              const link = window.location.origin + '/admin/facturation/devis';
              const shareText = buildQuoteShareText(link);
              if (navigator.share) { try { await navigator.share({ title: 'Devis', text: shareText }); } catch { } }
              else { try { await navigator.clipboard.writeText(shareText); alert('Texte copié'); } catch { alert('Partage non supporté'); } }
            }}>Partager</button>
          </div>
          <div className="searchbar quotes-search" style={{ marginLeft: 'auto' }}>
            <svg className="icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
            <input
              type="text"
              placeholder="Rechercher un devis..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Quotes grid */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="quotes-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
          {quotes
            .filter(q =>
              (q.quote_number?.toLowerCase() || '').includes(search.toLowerCase()) ||
              (customerNameOf(q).toLowerCase()).includes(search.toLowerCase())
            )
            .map((q) => (
              <div key={q.id} style={{ border: '1px solid var(--admin-border)', borderRadius: 12, padding: 12, background: 'linear-gradient(180deg,var(--admin-panel),var(--admin-panel-2))' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className="customer-avatar">
                    {(customerNameOf(q) || '—').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis' }}>{q.quote_number || '—'}</div>
                    <div style={{ color: 'var(--admin-muted)', fontSize: '.9rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{customerNameOf(q)}</div>
                    {companyNameOf(q) && (
                      <div style={{ color: 'var(--admin-muted)', fontSize: '.85rem', overflow: 'hidden', textOverflow: 'ellipsis' }}>{companyNameOf(q)}</div>
                    )}
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    <span style={{ padding: '4px 8px', borderRadius: 999, border: '1px solid var(--admin-border)', fontSize: '.8rem' }}>{statusLabel(q.status)}</span>
                  </div>
                </div>

                <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h18v18H3z" /></svg>
                    <span>Projet: {projectNameOf(q) || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 14H7v-2h10v2zm0-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg>
                    <span>Valide jusqu'au: {q.valid_until ? new Date(q.valid_until).toLocaleDateString() : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 12h18v2H3z" /></svg>
                    <span>Total HT: {typeof q.subtotal === 'number' ? `${q.subtotal.toFixed(2)}€` : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--admin-text)' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 5v14m7-7H5" /></svg>
                    <span>Total TTC: {typeof q.total === 'number' ? `${q.total.toFixed(2)}€` : '—'}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap', position: 'relative' }}>
                  <button
                    className="header-pill header-shape btn-muted has-tip"
                    data-tip="Aperçu PDF"
                    title="Aperçu PDF"
                    aria-label="Aperçu PDF"
                    onClick={() => handleQuotePDF(q, 'preview')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" /></svg>
                  </button>
                  <button
                    className="header-pill header-shape btn-muted has-tip"
                    data-tip="Télécharger PDF"
                    title="Télécharger PDF"
                    aria-label="Télécharger PDF"
                    onClick={() => handleQuotePDF(q, 'download')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" /></svg>
                  </button>
                  <button
                    className="header-pill header-shape btn-accent has-tip"
                    data-tip="Partager lien espace client"
                    title="Partager lien espace client"
                    aria-label="Partager lien espace client"
                    onClick={async () => {
                      const origin = window.location.origin;
                      const accountUrl = `${origin}/mon-compte`;
                      const shareText = buildQuoteShareText(accountUrl);
                      if (navigator.share) {
                        try { await navigator.share({ title: `Devis ${q.quote_number || ''}`.trim(), text: shareText, url: accountUrl }); } catch { }
                      } else {
                        try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { alert('Partage non supporté'); }
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" /></svg>
                  </button>
                  <button
                    className="header-pill header-shape btn-accent has-tip"
                    data-tip="Partager le devis"
                    title="Partager le devis"
                    aria-label="Partager le devis"
                    onClick={async () => {
                      const origin = window.location.origin;
                      const accountUrl = `${origin}/mon-compte`;
                      const shareText = buildQuoteDetailedShareText(q, accountUrl);
                      if (navigator.share) {
                        try { await navigator.share({ title: `Devis ${q.quote_number || ''}`.trim(), text: shareText, url: accountUrl }); } catch { }
                      } else {
                        try { await navigator.clipboard.writeText(shareText); alert('Message copié'); } catch { alert('Partage non supporté'); }
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89L8.78 12H6c0-3.86 3.14-7 7-7s7 3.14 7 7-3.14 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 20.51 10.51 21.5 13 21.5c4.97 0 9-4.03 9-9s-4.03-9-9-9z" /></svg>
                  </button>
                  <button
                    className="header-pill header-shape btn-muted has-tip"
                    data-tip="Dupliquer le devis"
                    title="Dupliquer"
                    aria-label="Dupliquer"
                    onClick={async () => {
                      const payload = {
                        customer_id: q.customer_id || undefined,
                        vehicle_id: q.vehicle_id || undefined,
                        status: 'draft',
                        valid_until: q.valid_until || null,
                        notes: q.notes || '',
                        items: Array.isArray(q.items) ? q.items : [],
                        discount_type: q.discount_type || '',
                        discount_value: Number(q.discount_value || 0),
                        discount_label: q.discount_label || '',
                        tax_enabled: typeof q.tax_enabled === 'boolean' ? q.tax_enabled : true,
                        subtotal: Number(q.subtotal || 0),
                        discount_total: Number(q.discount_total || 0),
                        tax_amount: Number(q.tax_amount || 0),
                        total: Number(q.total || 0),
                        tax_rate: Number(q.tax_rate || (settings?.default_tax_rate || 20)),
                      };
                      try { await handleCreate(payload); } catch { }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 1H4c-1.1 0-2 .9-2 2v12h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14l4-4h9c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2z" /></svg>
                  </button>
                  <button
                    className="header-pill header-shape btn-success has-tip"
                    data-tip="Marquer comme accepté"
                    title="Accepter"
                    aria-label="Accepter"
                    onClick={() => handleUpdate(q.id, { status: 'accepted' })}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                  </button>
                  <button
                    className="header-pill header-shape btn-danger has-tip"
                    data-tip="Marquer comme refusé"
                    title="Refuser"
                    aria-label="Refuser"
                    onClick={() => handleUpdate(q.id, { status: 'rejected' })}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
                  </button>
                  <button
                    className="header-pill header-shape btn-warning has-tip"
                    data-tip="Historique"
                    title="Historique"
                    aria-label="Historique"
                    onClick={() => { setHistoryQuote(q); loadQuoteHistory(q); }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6a7 7 0 1 1 2.06 4.94L7.64 18.36A9 9 0 1 0 13 3zm-1 4v5l4.25 2.52.75-1.23-3.5-2.08V7H12z" /></svg>
                  </button>
                  <div className="relative-inline" style={{ position: 'relative' }}>
                    <button
                      className="header-pill header-shape has-tip"
                      data-tip="Plus d'actions"
                      title="Actions du devis"
                      aria-label="Actions du devis"
                      onClick={(e) => { e.stopPropagation(); setMenuFor(prev => prev === q.id ? null : q.id); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                    </button>
                    {menuFor === q.id && (
                      <div className="add-menu" style={{ position: 'absolute', top: 36, left: 0, background: 'var(--admin-panel)', border: '1px solid var(--admin-border)', borderRadius: 8, padding: 8, boxShadow: '0 8px 18px rgba(0,0,0,0.18)', zIndex: 60, minWidth: 260 }} onClick={(e) => e.stopPropagation()}>
                        <button className="add-menu-item" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 8, background: 'transparent', color: 'var(--admin-text)', border: '1px solid transparent' }}
                          onClick={() => { setMenuFor(null); setPrefillInvoiceQuoteId(q.id); setEditingInvoice(null); setShowInvoiceForm(true); }}
                        >
                          Créer une facture à partir du devis
                        </button>
                      </div>
                    )}
                  </div>
                  <button className="header-pill header-shape btn-accent has-tip" data-tip="Modifier le devis" title="Modifier" aria-label="Modifier" onClick={() => { setEditingQuote(q); setOpen(true); }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" /></svg>
                  </button>
                  <button className="header-pill header-shape btn-danger has-tip" data-tip="Supprimer le devis" title="Supprimer" aria-label="Supprimer" onClick={() => handleDelete(q.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" /></svg>
                  </button>
                </div>
                <div style={{ textAlign: 'center', color: 'var(--admin-muted)', fontSize: '.85rem', marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--admin-border)' }}>Créé le {new Date(q.created).toLocaleDateString()}</div>
              </div>
            ))}

          {quotes.filter(q => (q.quote_number?.toLowerCase() || '').includes(search.toLowerCase()) || (customerNameOf(q).toLowerCase()).includes(search.toLowerCase())).length === 0 && !adminLoading && allowEmpty && (
            <div style={{ gridColumn: '1 / -1', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, width: '100%' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: .7, display: 'block' }}>
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <h3 style={{ margin: 0 }}>Aucun devis trouvé</h3>
                <p style={{ margin: 0, color: 'var(--admin-muted)' }}>
                  {search ? `Aucun résultat pour "${search}"` : 'Commencez par créer votre premier devis'}
                </p>
                {!search && (
                  <button
                    className="btn-primary"
                    onClick={() => { setEditingQuote(null); setOpen(true); }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px', borderRadius: 10,
                      background: 'linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85))',
                      border: '1px solid rgba(79,70,229,0.6)',
                      boxShadow: '0 6px 18px rgba(79,70,229,0.35)',
                      color: 'white'
                    }}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 999, background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                    </span>
                    <span>Nouveau devis</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <QuoteForm
          quote={editingQuote}
          customers={customers}
          vehicles={vehicles}
          services={services}
          parts={parts}
          settings={settings}
          onSubmit={(data) => editingQuote ? handleUpdate(editingQuote.id, data) : handleCreate(data)}
          onCancel={() => { setOpen(false); setEditingQuote(null); }}
          loading={loading}
          onCreateCustomer={async (data) => { const r = await pb.collection('customers').create(data); try { const list = await pb.collection('customers').getList(1, 200, { sort: '-created' }); setCustomers(list?.items ?? []); } catch { } return r; }}
          onCreateVehicle={async (data) => {
            // sanitize payload for PocketBase
            const cleaned = { ...data };
            // Force link to customer (compat across field names)
            const linkId = cleaned.client || cleaned.customer_id || cleaned.customer || (editingQuote?.customer_id) || null;
            if (linkId) {
              cleaned.client = linkId;
              cleaned.customer_id = linkId;
              cleaned.customer = linkId;
            }
            const allowedActions = ['Site web', 'E-commerce', 'SEO', 'SEA', 'Branding', 'Design', 'Développement', 'Maintenance', 'Formation', 'Licence', 'Autre'];
            if (Array.isArray(cleaned.actions)) {
              cleaned.actions = cleaned.actions
                .filter(v => typeof v === 'string' && v.trim())
                .filter(v => allowedActions.includes(v));
              if (cleaned.actions.length === 0) delete cleaned.actions;
            }
            // Auto-génération du code interne si absent
            const ensureCode = () => `PRJ-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`;
            if (cleaned.internal_code === undefined || cleaned.internal_code === null || cleaned.internal_code === '') {
              cleaned.internal_code = ensureCode();
            }
            if (cleaned.objectives === undefined || cleaned.objectives === null || cleaned.objectives === '') delete cleaned.objectives;
            if (cleaned.description === undefined || cleaned.description === null || cleaned.description === '') delete cleaned.description;
            if (cleaned.budget_min === '' || cleaned.budget_min === null) delete cleaned.budget_min; else cleaned.budget_min = Number(cleaned.budget_min);
            if (cleaned.budget_max === '' || cleaned.budget_max === null) delete cleaned.budget_max; else cleaned.budget_max = Number(cleaned.budget_max);
            // Tentatives en cas de collision d'unicité sur internal_code
            let attempts = 0;
            while (attempts < 5) {
              try {
                const r = await pb.collection('projects').create(cleaned);
                try { const list = await pb.collection('projects').getList(1, 200, { sort: '-created' }); setVehicles(list?.items ?? []); } catch (e) { console.warn('refresh projects failed', e?.message || e); }
                return r;
              } catch (e) {
                const codeErr = e?.data?.data?.internal_code?.code;
                if (codeErr === 'validation_not_unique') {
                  // régénérer et retenter
                  cleaned.internal_code = ensureCode();
                  attempts += 1;
                  continue;
                }
                console.error('Create project failed', e?.data || e?.message || e);
                const details = e?.data?.data ? JSON.stringify(e.data.data) : (e?.message || 'Erreur inconnue');
                try { alert(`Création projet impossible:\n${details}`); } catch { }
                throw e;
              }
            }
            // si toutes les tentatives ont échoué (très improbable)
            throw new Error('Impossible de générer un code interne unique après plusieurs tentatives');
          }}
          onCreateService={async (data) => { const r = await pb.collection('services_catalog').create(data); try { const list = await pb.collection('services_catalog').getList(1, 200, { sort: 'name' }); const mapped = (list?.items ?? []).map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false })); setServices(mapped); } catch { } return r; }}
          onCreatePart={async (data) => { const r = await pb.collection('products').create(data); try { const list = await pb.collection('products').getList(1, 200, { sort: 'name' }); const mapped = (list?.items ?? []).map(p => ({ id: p.id, name: p.name || p.title || 'Produit', price: Number(p.price || p.base_price || 0), sku: p.sku || '' })); setParts(mapped); } catch { } return r; }}
          defaultCustomerId={defaultCustomerIdFromQuery || customers[0]?.id}
        />
      )}

      {showQuotesGeneralQR && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) setShowQuotesGeneralQR(false); }}>
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h3>QR code – Espace client</h3>
              <button className="close-btn" onClick={() => setShowQuotesGeneralQR(false)} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body" style={{ display: 'grid', gap: 12 }}>
              {(() => {
                const url = `${window.location.origin}/mon-compte`; const qr = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=${encodeURIComponent(url)}`; return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <img src={qr} alt="QR Espace client" width={240} height={240} style={{ borderRadius: 12, border: '1px solid var(--admin-border)', background: 'white' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button className="header-pill header-shape btn-accent" onClick={async () => { try { await navigator.clipboard.writeText(url); alert('Lien copié'); } catch { } }}>Copier le lien</button>
                      <button className="header-pill header-shape" onClick={() => { try { const a = document.createElement('a'); a.href = qr; a.download = 'qr-espace-client.png'; document.body.appendChild(a); a.click(); a.remove(); } catch { } }}>Télécharger QR</button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {historyQuote && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target.classList.contains('modal-overlay')) setHistoryQuote(null); }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>Historique du devis {historyQuote.quote_number || ''}</h3>
              <button className="close-btn" onClick={() => setHistoryQuote(null)} aria-label="Fermer">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" /></svg>
              </button>
            </div>
            <div className="modal-body">
              {quoteLogsLoading ? (
                <div>Chargement...</div>
              ) : quoteLogs.length === 0 ? (
                <div>Aucun historique pour ce devis.</div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {quoteLogs.map(log => (
                    <div key={log.id} style={{ border: '1px solid var(--admin-border)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontWeight: 600 }}>{new Date(log.created).toLocaleString()}</div>
                      <div style={{ color: 'var(--admin-muted)' }}>{log.message || '-'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showInvoiceForm && (
        <InvoiceForm
          invoice={editingInvoice}
          customers={customers}
          vehicles={vehicles}
          services={services}
          parts={parts}
          settings={settings}
          quotes={quotes}
          onSubmit={handleCreateInvoice}
          onCancel={() => { setShowInvoiceForm(false); setEditingInvoice(null); setPrefillInvoiceQuoteId(''); }}
          loading={false}
          onCreateCustomer={async (data) => { const r = await pb.collection('customers').create(data); try { const list = await pb.collection('customers').getList(1, 200, { sort: '-created' }); setCustomers(list?.items ?? []); } catch { } return r; }}
          onCreateVehicle={async (data) => { const cleaned = { ...data }; const linkId = cleaned.client || cleaned.customer_id || cleaned.customer || customers[0]?.id || null; if (linkId) { cleaned.client = linkId; cleaned.customer_id = linkId; cleaned.customer = linkId; } const r = await pb.collection('projects').create(cleaned); try { const list = await pb.collection('projects').getList(1, 200, { sort: '-created' }); setVehicles(list?.items ?? []); } catch { } return r; }}
          onCreateService={async (data) => { const r = await pb.collection('services_catalog').create(data); try { const list = await pb.collection('services_catalog').getList(1, 200, { sort: 'name' }); const mapped = (list?.items ?? []).map(s => ({ id: s.id, name: s.name || s.title || 'Service', base_price: Number(s.base_price || s.price || 0), active: s.active !== false })); setServices(mapped); } catch { } return r; }}
          onCreatePart={async (data) => { const r = await pb.collection('products').create(data); try { const list = await pb.collection('products').getList(1, 200, { sort: 'name' }); const mapped = (list?.items ?? []).map(p => ({ id: p.id, name: p.name || p.title || 'Produit', price: Number(p.price || p.base_price || 0), sku: p.sku || '' })); setParts(mapped); } catch { } return r; }}
          defaultCustomerId={(quotes.find(q => q.id === prefillInvoiceQuoteId)?.customer_id) || customers[0]?.id}
          defaultQuoteId={prefillInvoiceQuoteId}
        />
      )}
    </AdminLayout>
  );
}

export default AdminBillingQuotesPage;
